import { useMemo, useEffect, useState, useCallback } from 'react';
import { Col, Row, Form } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { FilePdfOutlined } from '@ant-design/icons';

import { useParams } from 'react-router-dom';

import CandidatesApi from 'libs/api/cv/candidates';
import { useApi } from 'hooks/useApi';
import moment from 'moment';
import CVFilters from '../../../../libs/api/cv/cv';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from '../../../../hooks/useUser';
import { getShops } from '../../../../hooks/useShops';
import LinkWithParams from '../../../../components/linkWithParams';
import SsoUsers from '../../../../libs/api/ssoUsers';
import UIHelper from '../../../../helpers/UIHelper';
import { useNavigate } from '../../../../hooks/useNavigate';
import Helpers from '../../../../helpers';

const api = new CandidatesApi(true);
const filtersApi = new CVFilters();
const usersApi = new SsoUsers();

const useCandidatesAuto = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const navigate = useNavigate();
  const shops = useRecoilValue(getShops);
  const [filtersData, setFiltersData] = useState({
    vacancy_categories: [],
    experience_years: [],
    birthday_range: [],
    contract_types: [],
    regions: [],
    status: [],
    vacancy_application_types: [],
    vacancy_languages: [],
    vacancy_language_levels: [],
    vacancy_language2_levels: [],
  });
  const {
    loading,
    data,
    singleItem,
    setUpdateInProcess,
    fetchList,
    onChangeTableFilter,
    updateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'candidates',
    singleResourceKey: 'candidate',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
        render: (row, data) => data['shopping_center'],
      },
      {
        title: t('ID Candidato'),
        dataIndex: 'candidatures.id',
        sorter: () => {},
        render: (row, data) => data['candidature_id'],
      },
      {
        title: t('Cognome'),
        dataIndex: 'users.surname',
        sorter: () => {},
        render: (row, data) => data['surname'],
      },
      {
        title: t('Nome'),
        dataIndex: 'users.name',
        sorter: () => {},
        render: (row, data) => data['name'],
      },
      {
        title: t('Settore'),
        dataIndex: 'vacancy_application_types.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_application_type?.title,
      },
      {
        title: t('Profilo candidato'),
        dataIndex: 'vacancy_categories.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_category?.title,
      },
      {
        title: t('Sesso'),
        dataIndex: 'users.sex',
        sorter: () => {},
        render: (data, row) => row?.sex_short,
      },
      {
        title: t('Fascia d’età'),
        dataIndex: 'users.birthday',
        sorter: () => {},
        render: (data, row) => row.birthday_range_title,
      },
      {
        title: t('Anni esp'),
        dataIndex: 'experience_years.title',
        sorter: () => {},
        render: (data, row) => row?.experience_year?.title,
      },
      {
        title: t('Disponibilita` contratto'),
        dataIndex: 'contract_types.title',
        sorter: () => {},
        render: (data, row) => row?.contract_type?.title,
      },
      {
        title: t('Provincia'),
        dataIndex: 'regions.title',
        sorter: () => {},
        render: (data, row) => row?.region?.title,
      },

      {
        title: t('Lingua 1'),
        dataIndex: 'vacancy_languages.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language?.title,
      },
      {
        title: t('Livello lingua 1'),
        dataIndex: 'users.vacancy_language_level',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language_level,
      },
      {
        title: t('Lingua 2'),
        dataIndex: 'vacancy_language2s_users.title',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language2?.title,
      },
      {
        title: t('Livello lingua 2'),
        dataIndex: 'users.vacancy_language2_level',
        sorter: () => {},
        render: (data, row) => row?.vacancy_language2_level,
      },
      {
        title: t('Whishlist'),
        dataIndex: 'wishlist',
        render: (data) => (data ? 'Si' : 'No'),
      },

      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        // width: 120,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <LinkWithParams to={`${pathname}/${row.candidature_id}/view`}>
                <Button>Apri</Button>
              </LinkWithParams>
            </Col>
            {row.can_update && (
              <Col>
                <Link to={`${pathname}/${row.candidature_id}/delete`}>
                  <Button type={'red'} icon={<DeleteIcon />} />
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center_id',
        values: shoppingCenters.map((item) => ({
          title: item.title,
          value: item.id,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_text',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Settore'),
        type: 'select',
        name: 'by_app_type',
        values: filtersData.vacancy_application_types.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Profilo candidato'),
        type: 'select',
        name: 'by_vacancy_category',
        values: filtersData.vacancy_categories.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Sesso'),
        type: 'select',
        name: 'by_sex',
        values: [
          { value: 1, title: 'M' },
          { value: 2, title: 'F' },
        ],
      },
      {
        title: t('Fascia di età'),
        type: 'select',
        name: 'by_range_birthday',
        values: filtersData.birthday_range.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Anni di esperienza'),
        type: 'select',
        name: 'by_experience_years_id',
        values: filtersData.experience_years.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Disponibilità contratto'),
        type: 'select',
        name: 'by_contract_type_id',
        values: filtersData.contract_types.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Provincia'),
        type: 'select',
        name: 'by_region_id',
        isSearch: true,
        values: filtersData.regions.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Lingua 1'),
        type: 'select',
        name: 'by_vacancy_language_id',
        values: filtersData.vacancy_languages.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Livello lingua 1'),
        type: 'select',
        name: 'by_vacancy_language_level',
        values: filtersData.vacancy_language_levels.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Lingua 2'),
        type: 'select',
        name: 'by_vacancy_language2_id',
        values: filtersData.vacancy_languages.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Livello lingua 2'),
        type: 'select',
        name: 'by_vacancy_language2_level',
        values: filtersData.vacancy_language2_levels.map((item) => ({
          title: item.title,
          value: item.value,
        })),
      },
      {
        title: t('Wishlist'),
        type: 'select',
        name: 'in_my_wishlist',
        values: [
          { value: 'true', title: 'Si' },
          { value: 'false', title: 'No' },
        ],
      },
    ],
    [t, filtersData, shoppingCenters, shops],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem?.title) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);
  useEffect(() => {
    (async () => {
      try {
        const filters = await filtersApi.list();
        setFiltersData(filters.data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const onDelete = async (id) => {
    setUpdateInProcess(true);
    try {
      await api.autoDestroy(id);
      navigate(UIHelper.getListRoute(pathname));
      fetchList();
    } catch (e) {
    } finally {
      setUpdateInProcess(false);
    }
  };
  const handleDownloadPdf = useCallback(
    async (fileName = 'vacancy_application.pdf', url) => {
      try {
        // const {
        //   data: { file_url },
        // } = await api.getPDFUrl(url);
        const response = await api.getPDF(url);

        Helpers.downloadFile(response, fileName);
      } catch (e) {
        console.log(e);
        UIHelper.serverError();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleAddWishList = async (data) => {
    setUpdateInProcess(true);
    try {
      const response = await api.addWishList(id);
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    singleItem,
    filters,
    handleDownloadExcel,
    updateInProcess,
    loading,
    onChangeTableFilter,
    onDelete,
    handleDownloadPdf,
    handleAddWishList,
  };
};

export default useCandidatesAuto;
